import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminDashboardLayout from '../../components/layout/AdminDashboardLayout';
import DataTable from '../../components/common/DataTable';
import usePageTitle from '../../hooks/usePageTitle';
import useSmartRefresh from '../../hooks/useSmartRefresh';
import subscriptionService from '../../services/subscriptionService';
import { toast } from 'react-hot-toast';

/**
 * Subscriptions Management Page - Create, edit, delete and manage subscription plans
 */
const SubscriptionsPage = () => {
  // Set page title
  usePageTitle('Subscriptions Management');

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    plan_type: 'basic',
    description: '',
    price: '',
    imei_checks_limit: '',
    duration: 30,
    features: [''],
    is_active: true,
  });

  // Data fetching function
  const fetchSubscriptionsData = async () => {
    try {
      // Fetch subscription plans and stats
      const [plansResponse] = await Promise.all([
        subscriptionService.getAdminPlans(),
        subscriptionService.getSubscriptionStats(),
      ]);

      // Handle plans response (DRF paginated response)
      if (plansResponse && plansResponse.results) {
        setSubscriptions(plansResponse.results);
      } else if (Array.isArray(plansResponse)) {
        // Handle direct array response
        setSubscriptions(plansResponse);
      }

      // Stats are now calculated from the plans data directly
    } catch (error) {
      console.error('Error fetching subscriptions data:', error);
      toast.error('Failed to load subscriptions data');
    } finally {
    }
  };

  const { isRefreshing, refreshData } = useSmartRefresh(
    fetchSubscriptionsData,
    'Subscriptions refreshed successfully',
  );

  // Load data on component mount
  useEffect(() => {
    fetchSubscriptionsData();
  }, []);

  // Calculate summary stats from subscriptions data
  const totalPlans = subscriptions.length;
  const activePlans = subscriptions.filter((sub) => sub.is_active).length;
  const totalSubscribers = subscriptions.reduce(
    (sum, sub) => sum + (sub.subscriber_count || 0),
    0,
  );
  const monthlyRevenue = subscriptions.reduce(
    (sum, sub) =>
      sum + parseFloat(sub.price || 0) * (sub.subscriber_count || 0),
    0,
  );

  // Define table columns
  const columns = [
    {
      id: 'name',
      label: 'Plan Name',
      sortable: true,
      width: '25%',
      render: (value, item) => (
        <div className="flex items-center">
          <div
            className={`w-3 h-3 rounded-full mr-3 ${item.is_active ? 'bg-success-500' : 'bg-gray-400'}`}
          ></div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {value}
              </span>
              {item.is_popular && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  Popular
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {item.description}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'price',
      label: 'Price',
      sortable: true,
      align: 'center',
      width: '15%',
      render: (value, item) => (
        <div className="text-center">
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            ${value}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            /{item.duration} days
          </div>
        </div>
      ),
    },
    {
      id: 'subscriber_count',
      label: 'Subscribers',
      sortable: true,
      align: 'center',
      width: '15%',
      render: (value) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {value?.toLocaleString() || 0}
        </span>
      ),
    },
    {
      id: 'is_active',
      label: 'Status',
      sortable: true,
      align: 'center',
      width: '15%',
      render: (value) => (
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            value
              ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
          }`}
        >
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'updated_at',
      label: 'Last Updated',
      sortable: true,
      width: '30%',
      render: (value) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(value).toLocaleDateString()}
        </div>
      ),
    },
  ];

  // Define table actions
  const actions = [
    {
      type: 'view',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      tooltip: 'View Details',
      variant: 'primary',
    },
    {
      type: 'edit',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
          />
        </svg>
      ),
      tooltip: 'Edit Plan',
    },
    {
      type: 'delete',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
          />
        </svg>
      ),
      tooltip: 'Delete Plan',
      variant: 'danger',
    },
  ];

  // Define filters
  const filters = [
    {
      key: 'is_active',
      label: 'Status',
      options: [
        { value: true, label: 'Active' },
        { value: false, label: 'Inactive' },
      ],
    },
    {
      key: 'plan_type',
      label: 'Plan Type',
      options: [
        { value: 'basic', label: 'Basic' },
        { value: 'professional', label: 'Professional' },
        { value: 'enterprise', label: 'Enterprise' },
      ],
    },
  ];

  // Action handlers
  const handleView = (subscription) => {
    setSelectedSubscription(subscription);
    setIsViewModalOpen(true);
  };

  const handleEdit = (subscription) => {
    setSelectedSubscription(subscription);
    setFormData({
      name: subscription.name,
      plan_type: subscription.plan_type,
      description: subscription.description,
      price: subscription.price.toString(),
      imei_checks_limit: subscription.imei_checks_limit.toString(),
      duration: subscription.duration,
      features: subscription.features || [''],
      is_active: subscription.is_active,
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (subscription) => {
    if (
      window.confirm(
        `Are you sure you want to delete the "${subscription.name}" plan?`,
      )
    ) {
      try {
        setActionLoading(true);
        const response = await subscriptionService.deletePlan(subscription.id);

        if (response.success) {
          toast.success('Plan deleted successfully');
          await fetchSubscriptionsData(); // Refresh data
        } else {
          toast.error(response.message || 'Failed to delete plan');
        }
      } catch (error) {
        console.error('Error deleting plan:', error);
        toast.error('Failed to delete plan');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleCreateNew = () => {
    setFormData({
      name: '',
      plan_type: 'basic',
      description: '',
      price: '',
      imei_checks_limit: '',
      duration: 30,
      features: [''],
      is_active: true,
    });
    setIsCreateModalOpen(true);
  };

  // Modal close handlers
  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedSubscription(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormData({
      name: '',
      plan_type: 'basic',
      description: '',
      price: '',
      imei_checks_limit: '',
      duration: 30,
      features: [''],
      is_active: true,
    });
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedSubscription(null);
    setFormData({
      name: '',
      plan_type: 'basic',
      description: '',
      price: '',
      imei_checks_limit: '',
      duration: 30,
      features: [''],
      is_active: true,
    });
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData((prev) => ({
      ...prev,
      features: newFeatures,
    }));
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ''],
    }));
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      features: newFeatures,
    }));
  };

  const handleSavePlan = async () => {
    try {
      setActionLoading(true);

      // Validate form data
      if (!formData.name || !formData.price || !formData.imei_checks_limit) {
        toast.error('Please fill in all required fields');
        return;
      }

      const planData = {
        name: formData.name,
        plan_type: formData.plan_type,
        description: formData.description,
        price: parseFloat(formData.price),
        imei_checks_limit: parseInt(formData.imei_checks_limit),
        duration: formData.duration,
        features: formData.features.filter((f) => f.trim() !== ''),
        is_active: formData.is_active,
      };

      let response;
      if (isEditModalOpen && selectedSubscription) {
        response = await subscriptionService.updatePlan(
          selectedSubscription.id,
          planData,
        );
      } else {
        response = await subscriptionService.createPlan(planData);
      }

      if (response.success) {
        toast.success(
          `Plan ${isEditModalOpen ? 'updated' : 'created'} successfully`,
        );
        if (isEditModalOpen) {
          handleCloseEditModal();
        } else {
          handleCloseCreateModal();
        }
        await fetchSubscriptionsData(); // Refresh data
      } else {
        toast.error(
          response.message ||
            `Failed to ${isEditModalOpen ? 'update' : 'create'} plan`,
        );
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(`Failed to ${isEditModalOpen ? 'update' : 'create'} plan`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AdminDashboardLayout pageName="Subscriptions Management">
      <div className="space-y-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Create, edit, and manage subscription plans for the platform
          </p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Plans
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {totalPlans}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {activePlans} active
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-success-600 dark:text-success-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Subscribers
                </p>
                <p className="text-2xl font-bold text-success-600 dark:text-success-400">
                  {totalSubscribers.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Active subscriptions
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-emerald-600 dark:text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Monthly Revenue
                </p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  ${monthlyRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Projected monthly
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-orange-600 dark:text-orange-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Popular Plan
                </p>
                <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                  {subscriptions.find((sub) => sub.is_popular)?.name || 'None'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {subscriptions.find((sub) => sub.is_popular)
                    ?.subscriber_count || 0}{' '}
                  subscribers
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Create New Button */}
        <motion.div
          className="flex justify-end"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 text-white rounded-lg font-medium transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            Create New Plan
          </button>
        </motion.div>

        {/* DataTable Component */}
        <DataTable
          title="Subscription Plans Management"
          columns={columns}
          data={subscriptions}
          actions={actions}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={refreshData}
          searchPlaceholder="Search subscription plans..."
          filters={filters}
          rowsPerPageOptions={[5, 10, 20, 50]}
          defaultRowsPerPage={10}
          exportEnabled={true}
          refreshEnabled={true}
          isLoading={isRefreshing}
          className="mt-6"
        />

        {/* View Modal */}
        <AnimatePresence>
          {isViewModalOpen && selectedSubscription && (
            <motion.div
              className="fixed inset-0 z-50 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
                onClick={handleCloseViewModal}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              <div className="flex items-center justify-center min-h-screen px-4 py-8">
                <motion.div
                  className="relative w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <h2 className="text-2xl font-semibold text-primary-600 dark:text-primary-400">
                        Plan Details
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        View subscription plan information
                      </p>
                    </div>
                    <button
                      onClick={handleCloseViewModal}
                      className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Basic Information */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Basic Information
                        </h3>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Plan Name
                          </label>
                          <p className="text-gray-900 dark:text-gray-100 font-medium">
                            {selectedSubscription.name}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Plan Type
                          </label>
                          <p className="text-gray-900 dark:text-gray-100 capitalize">
                            {selectedSubscription.plan_type}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Price
                          </label>
                          <p className="text-gray-900 dark:text-gray-100 font-semibold text-lg">
                            ${selectedSubscription.price}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Status
                          </label>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              selectedSubscription.is_active
                                ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                            }`}
                          >
                            {selectedSubscription.is_active
                              ? 'Active'
                              : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      {/* Plan Details */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Plan Details
                        </h3>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            IMEI Checks Limit
                          </label>
                          <p className="text-gray-900 dark:text-gray-100 font-medium">
                            {selectedSubscription.imei_checks_limit?.toLocaleString()}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Duration
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">
                            {selectedSubscription.duration} days
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Subscribers
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">
                            {selectedSubscription.subscriber_count?.toLocaleString() ||
                              0}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Created
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">
                            {new Date(
                              selectedSubscription.created_at,
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mt-6">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Description
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {selectedSubscription.description}
                      </p>
                    </div>

                    {/* Features */}
                    <div className="mt-6">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Features
                      </label>
                      <ul className="mt-2 space-y-1">
                        {selectedSubscription.features?.map(
                          (feature, index) => (
                            <li
                              key={index}
                              className="flex items-center text-gray-900 dark:text-gray-100"
                            >
                              <svg
                                className="w-4 h-4 text-success-500 mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {feature}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                    <button
                      onClick={() => {
                        handleCloseViewModal();
                        handleEdit(selectedSubscription);
                      }}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 text-white rounded-lg font-medium transition-colors duration-200"
                    >
                      Edit Plan
                    </button>
                    <button
                      onClick={handleCloseViewModal}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create/Edit Modal */}
        <AnimatePresence>
          {(isCreateModalOpen || isEditModalOpen) && (
            <motion.div
              className="fixed inset-0 z-50 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
                onClick={
                  isEditModalOpen
                    ? handleCloseEditModal
                    : handleCloseCreateModal
                }
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              <div className="flex items-center justify-center min-h-screen px-4 py-8">
                <motion.div
                  className="relative w-full max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <h2 className="text-2xl font-semibold text-primary-600 dark:text-primary-400">
                        {isEditModalOpen ? 'Edit Plan' : 'Create New Plan'}
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {isEditModalOpen
                          ? 'Update subscription plan details'
                          : 'Create a new subscription plan'}
                      </p>
                    </div>
                    <button
                      onClick={
                        isEditModalOpen
                          ? handleCloseEditModal
                          : handleCloseCreateModal
                      }
                      className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Basic Information */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Basic Information
                        </h3>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Plan Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) =>
                              handleInputChange('name', e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Enter plan name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Plan Type *
                          </label>
                          <select
                            value={formData.plan_type}
                            onChange={(e) =>
                              handleInputChange('plan_type', e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          >
                            <option value="basic">Basic</option>
                            <option value="professional">Professional</option>
                            <option value="enterprise">Enterprise</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Price *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.price}
                            onChange={(e) =>
                              handleInputChange('price', e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="0.00"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            IMEI Checks Limit *
                          </label>
                          <input
                            type="number"
                            value={formData.imei_checks_limit}
                            onChange={(e) =>
                              handleInputChange(
                                'imei_checks_limit',
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Enter limit"
                          />
                        </div>
                      </div>

                      {/* Plan Details */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Plan Details
                        </h3>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Duration (days)
                          </label>
                          <input
                            type="number"
                            value={formData.duration}
                            onChange={(e) =>
                              handleInputChange(
                                'duration',
                                parseInt(e.target.value),
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="30"
                          />
                        </div>

                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.is_active}
                              onChange={(e) =>
                                handleInputChange('is_active', e.target.checked)
                              }
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Active Plan
                            </span>
                          </label>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Description
                          </label>
                          <textarea
                            value={formData.description}
                            onChange={(e) =>
                              handleInputChange('description', e.target.value)
                            }
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Enter plan description"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Features
                      </label>
                      <div className="space-y-2">
                        {formData.features.map((feature, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2"
                          >
                            <input
                              type="text"
                              value={feature}
                              onChange={(e) =>
                                handleFeatureChange(index, e.target.value)
                              }
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              placeholder="Enter feature"
                            />
                            {formData.features.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFeature(index)}
                                className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addFeature}
                          className="flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium"
                        >
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Add Feature
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                    <button
                      onClick={
                        isEditModalOpen
                          ? handleCloseEditModal
                          : handleCloseCreateModal
                      }
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePlan}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading
                        ? 'Saving...'
                        : isEditModalOpen
                          ? 'Update Plan'
                          : 'Create Plan'}
                    </button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminDashboardLayout>
  );
};

export default SubscriptionsPage;
